// Core
import React, { Suspense } from 'react';
import { Route, Switch, Redirect } from 'react-router-dom';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

// Layout
import Loading from './layout/elements/Loading';

// Views
// GLOBAL - not logged in
const UnsupportedBrowser = React.lazy(() => import('./views/UnsupportedBrowser'));
const Maintenance = React.lazy(() => import('./views/Maintenance'));
const Login = React.lazy(() => import('./views/Login'));
const ForgotPassword = React.lazy(() => import('./views/ForgotPassword'));
const PasswordRegistration = React.lazy(() => import('./views/PasswordRegistration'));

// GLOBAL - logged in
const UserSettings = React.lazy(() => import('./views/GlobalLogged/UserSettings'));
const Swagger = React.lazy(() => import('./views/GlobalLogged/Swagger'));

// ADMIN PANEL
const Customers = React.lazy(() => import('./views/AdminPanel/Customers'));
const Equipment = React.lazy(() => import('./views/AdminPanel/Equipment'));
const PreRegistration = React.lazy(() => import('./views/AdminPanel/PreRegistration'));
const Monitoring = React.lazy(() => import('./views/AdminPanel/Monitoring/Monitoring'));

// CUSTOMER PANEL
const Sites = React.lazy(() => import('./views/CustomerPanel/Sites'));
const Site = React.lazy(() => import('./views/CustomerPanel/Site'));
const Area = React.lazy(() => import('./views/CustomerPanel/Area'));
const Users = React.lazy(() => import('./views/CustomerPanel/Users'));
const UserGroups = React.lazy(() => import('./views/CustomerPanel/UserGroups'));
const UserLogs = React.lazy(() => import('./views/CustomerPanel/UserLogs'));
const Conveyor = React.lazy(() => import('./views/CustomerPanel/Conveyor'));
const ConveyorModule = React.lazy(() => import('./views/CustomerPanel/ConveyorModule'));
const Settings = React.lazy(() => import('./views/CustomerPanel/Settings'));
const CustomerPreRegistration = React.lazy(() => import('./views/CustomerPanel/PreRegistration'));
const MonitoringDiagram = React.lazy(() => import('./views/CustomerPanel/MonitoringDiagram'));
const Reports = React.lazy(() => import('./views/CustomerPanel/Reports/Reports'));
const MonitoringList = React.lazy(() => import('./views/CustomerPanel/MonitoringList'));
const MonitoringInspection = React.lazy(() => import('./views/CustomerPanel/MonitoringInspection'));
const MonitoringAlarmSummary = React.lazy(() => import('./views/CustomerPanel/MonitoringAlarmSummary'));
const MonitoringGateways = React.lazy(() => import('./views/CustomerPanel/MonitoringGateways'));
const MonitoringConveyorModule = React.lazy(() => import('./views/CustomerPanel/MonitoringConveyorModule'));
const ConveyorPosition = React.lazy(() => import('./views/CustomerPanel/ConveyorPosition/ConveyorPosition'));
const PropXList = React.lazy(() => import('./views/CustomerPanel/PropXList'));
const PropXMap = React.lazy(() => import('./views/CustomerPanel/PropXMap'));
const PropXAlarmSummary = React.lazy(() => import('./views/CustomerPanel/PropXAlarmSummary'));
const PropXDashboard = React.lazy(() => import('./views/CustomerPanel/PropXDashboard'));

// EXPERIMENTAL - TEMP
const PivotAutomation = React.lazy(() => import('./views/Experimental/PivotAutomation'));

// The component
const Routes = (props) => {
  const { userIsLoggedIn, user } = props;

  // Set which element will be rendering
  const routeElements = [];

  // LOGGED USERS
  if (userIsLoggedIn) {
    routeElements.push(
      // ROOT
      <Route path="/customers" component={Customers} key="root" />,
      (
        <Route path="/" exact key="rootRedirect">
          <Redirect to="/customers" />
        </Route>
      ),

      // GLOBAL - logged in
      <Route path="/user-settings" component={UserSettings} key="user-settings" />,
      <Route path="/api" component={Swagger} key="swagger" />,

      // CUSTOMER PANEL
      (
        <Route path="/system-info" exact key="systemInfo">
          <Redirect to="/settings/system-info" />,
        </Route>
      ),
      <Route path="/settings" component={Settings} key="settings" />,
      <Route path="/reports" component={Reports} key="reports" />,
      <Route
        path="/monitoring/diagram/conveyor-module/:idConveyorModule"
        component={MonitoringConveyorModule}
        key="monitoringConveyorModule"
      />,
      <Route path="/monitoring/diagram" component={MonitoringDiagram} key="monitoringDiagram" />,
      <Route path="/monitoring/list" component={MonitoringList} key="monitoringList" />,
      <Route path="/monitoring/inspection" component={MonitoringInspection} key="monitoringInspection" />,
      <Route path="/monitoring/alarm-summary" component={MonitoringAlarmSummary} key="monitoringAlarmSummary" />,
      <Route path="/monitoring/gateways" component={MonitoringGateways} key="monitoringGateways" />,
      <Route path="/users" component={Users} key="users" />,
      <Route path="/user-groups" component={UserGroups} key="userGroups" />,
      <Route path="/user-logs" component={UserLogs} key="userLogs" />,
      <Route path="/sites" component={Sites} key="sites" />,
      <Route path="/site/:idSite" component={Site} key="site" />,
      <Route path="/area/:idArea" component={Area} key="area" />,
      <Route exact path="/conveyor/:idConveyor" component={Conveyor} key="conveyor" />,
      <Route
        exact
        path="/conveyor/conveyor-module/:idConveyorModule"
        component={ConveyorModule}
        key="conveyorModule"
      />,
      <Route exact path="/conveyor/position/:idConveyorIdler/:position" component={ConveyorPosition} key="roll" />,
      <Route path="/customer/pre-registration" component={CustomerPreRegistration} key="customerPreRegistration" />,
      <Route exact path="/propx/devices/" component={PropXList} key="propXList" />,
      <Route exact path="/propx/devices/list" component={PropXList} key="propXList2" />,
      <Route exact path="/propx/devices/map" component={PropXMap} key="propXMap" />,
      <Route exact path="/propx/devices/:idDevice" component={PropXDashboard} key="propXDashboard" />,
      <Route path="/propx/alarm-summary" component={PropXAlarmSummary} key="propXAlarmSummary" />,
    );

    // ADMIN PANEL
    if (user['custom:isSuperior'] === '1') {
      routeElements.push(
        <Route path="/equipment" component={Equipment} key="equipment" />,
        <Route path="/pre-registration" component={PreRegistration} key="preRegistration" />,
        <Route path="/admin/monitoring" component={Monitoring} key="adminMonitoring" />,

        // EXPERIMENTAL
        <Route path="/pivot" component={PivotAutomation} key="pivotAutomation" />,
      );
    }

    // NOT LOGGED USERS
  } else {
    routeElements.push(
      (
        <Route path="/" exact key="login">
          <Login />
        </Route>
      ),
      <Route path="/forgot-password" component={ForgotPassword} key="forgotPassword" />,
    );
  }

  // GLOBAL - not logged in
  routeElements.push(
    <Route path="/register-password" component={PasswordRegistration} key="passwordRegistration" />,
    <Route path="/unsupported-browser" component={UnsupportedBrowser} key="unsupportedBrowser" />,
    <Route path="/maintenance" component={Maintenance} key="maintenance" />,
    (
      <Route key="catchAll">
        <Redirect to="/" />
      </Route>
    ),
  );

  return (
    <Suspense fallback={<Loading className="mv4" />}>
      <Switch>{routeElements}</Switch>
    </Suspense>
  );
};

// Mapping state
const mapStateToProps = (state) => ({
  userIsLoggedIn: state.auth.isLogged,
  user: state.auth.user,
});

// Prop Types
Routes.propTypes = {
  userIsLoggedIn: PropTypes.bool.isRequired,
  user: PropTypes.object.isRequired,
};

export default connect(mapStateToProps)(Routes);
